
import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MapPin, Users, Clock, Signal, Calendar, CheckCircle2, XCircle, Building2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import { useEffect } from "react";
import { useToast } from "@/hooks/use-toast";

const WorkLocationOverview = () => {
  const { toast } = useToast();
  const { data: locations, isLoading, refetch } = useQuery({
    queryKey: ["work-locations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("work_locations")
        .select(`
          id,
          title,
          radius,
          created_at,
          time_registrations (
            id,
            status,
            start_time,
            end_time,
            user_id,
            profiles!time_registrations_user_id_fkey (
              first_name,
              last_name
            )
          ),
          tasks (
            id,
            status,
            title,
            priority
          )
        `);

      if (error) {
        console.error("Error fetching work locations:", error);
        throw error;
      }
      return data || [];
    },
  });

  // Opsæt real-time subscription
  useEffect(() => {
    const channel = supabase
      .channel('work-locations-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // Lytter til alle ændringer (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'work_locations'
        },
        async (payload) => {
          console.log('Realtime update received:', payload);
          
          // Vis notifikation baseret på ændringstype
          const actionText = {
            INSERT: 'tilføjet',
            UPDATE: 'opdateret',
            DELETE: 'slettet'
          }[payload.eventType];

          toast({
            title: `Arbejdslokation ${actionText}`,
            description: payload.eventType === 'DELETE' 
              ? 'En arbejdslokation er blevet slettet'
              : `${payload.new?.title || 'En arbejdslokation'} er blevet ${actionText}`,
          });

          // Opdater data
          await refetch();
        }
      )
      .subscribe();

    // Tilføj også subscription til tidsregistreringer
    const timeRegChannel = supabase
      .channel('time-registrations-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'time_registrations'
        },
        async () => {
          console.log('Time registration update received');
          await refetch();
        }
      )
      .subscribe();

    // Cleanup ved unmount
    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(timeRegChannel);
    };
  }, [refetch, toast]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-48" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {locations?.map((location) => (
        <TooltipProvider key={location.id}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-lg flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-blue-500" />
                        {location.title}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Oprettet {format(new Date(location.created_at), "d. MMMM yyyy", { locale: da })}
                      </p>
                    </div>
                    <Badge 
                      variant={location.radius <= 50 ? "default" : "secondary"}
                      className="h-6"
                    >
                      {location.radius}m radius
                    </Badge>
                  </div>

                  <div className="grid gap-3">
                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium">Aktive medarbejdere</span>
                      </div>
                      <Badge variant="secondary">
                        {location.time_registrations?.filter(tr => tr.status === 'active').length || 0}
                      </Badge>
                    </div>

                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-sm font-medium">Aktive opgaver</span>
                      </div>
                      <Badge variant="secondary">
                        {location.tasks?.filter(task => task.status === 'active').length || 0}
                      </Badge>
                    </div>

                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                      <div className="flex items-center gap-2">
                        <Signal className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium">GPS Signal</span>
                      </div>
                      <Badge variant="secondary" className={location.radius <= 50 ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}>
                        {location.radius <= 50 ? "God dækning" : "Begrænset dækning"}
                      </Badge>
                    </div>
                  </div>

                  {location.tasks && location.tasks.length > 0 && (
                    <div className="border-t pt-3">
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        Seneste opgaver
                      </h4>
                      <div className="space-y-1">
                        {location.tasks.slice(0, 2).map((task) => (
                          <div key={task.id} className="flex items-center justify-between text-sm">
                            <span className="truncate">{task.title}</span>
                            <Badge variant={task.priority === 'high' ? 'destructive' : 'default'} className="ml-2">
                              {task.priority}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </TooltipTrigger>
            <TooltipContent>
              <p>Klik for at se flere detaljer</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}
    </div>
  );
};

export default WorkLocationOverview;
